import React, { useState } from 'react';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  PrevIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
  isLnatPearsonExam,
  isEsatPearsonExam,
  isTaraPearsonExam,
  getPearsonPlayerExamTitle,
} from './TmuaPearsonShell';
import TmuaPearsonDialog from './TmuaPearsonDialog';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';

const TmuaWelcomeScreen = ({
  examName = 'TMUA',
  title,
  examHeading,
  tsaSitting = false,
  heritageExam,
  ndaVariant,
  lsatPractice = false,
  disclaimerCopy,
  onNext,
  onEndExam,
  onPrevious,
  colorScheme = 'pearsonDefault',
  setColorScheme,
  readyToBeginOpen = false,
  onReadyYes,
  onReadyNo,
}) => {
  const [showEndExam, setShowEndExam] = useState(false);
  const T = TMUA_PEARSON_TOKENS;
  const isLnat = isLnatPearsonExam(examName);
  const isEsat = isEsatPearsonExam(examName);
  const isTara = isTaraPearsonExam(examName);
  const nextVariant = isLnat || isEsat || isTara ? 'chrome' : 'yellow';
  const nextIconColor = isLnat || isEsat || isTara ? '#FFFFFF' : T.nextDefaultFg;
  const overlayOpen = showEndExam || readyToBeginOpen;

  useTmuaPearsonAccessKeys(
    {
      showEndExamModalVisible: showEndExam,
      showReadyToBeginVisible: Boolean(readyToBeginOpen),
      nextAvailable: !overlayOpen,
      endExamAvailable: !readyToBeginOpen,
    },
    {
      onNext: () => { if (!overlayOpen) onNext(); },
      onEndExam: () => setShowEndExam(true),
      onConfirmYes: () => {
        if (readyToBeginOpen && typeof onReadyYes === 'function') onReadyYes();
        else if (showEndExam) onEndExam();
      },
      onConfirmNo: () => {
        if (readyToBeginOpen && typeof onReadyNo === 'function') onReadyNo();
        else setShowEndExam(false);
      },
    }
  );

  return (
    <TmuaPearsonShell
      examName={isLnat || isEsat || isTara ? examName : 'TMUA'}
      title={title || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}
      tsaSitting={tsaSitting}
      heritageExam={heritageExam}
      colorSchemeLabel={isLnat ? getPearsonColorSchemeDropdownLabel(examName) : undefined}
      onBackToSelection={onEndExam}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={() => setShowEndExam(true)} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <>
          {isLnat && typeof onPrevious === 'function' && !readyToBeginOpen ? (
            <TmuaPearsonFooterButton onClick={onPrevious} icon={<PrevIcon color={T.titleBarFg} />}>
              Previous
            </TmuaPearsonFooterButton>
          ) : null}
          <TmuaPearsonFooterButton variant={nextVariant} onClick={onNext} icon={<NextIcon color={nextIconColor} />} iconPosition="end" underline="n">
            Next
          </TmuaPearsonFooterButton>
        </>
      )}
    >
      <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        {isLnat ? (
          <div className="tmua-pearson-instruction-copy" style={{ width: '100%', textAlign: 'left', color: '#000' }}>
            <p style={{ margin: '0 0 12px', textTransform: 'uppercase', fontWeight: 700, color: '#000' }}>
              {lsatPractice || ndaVariant === 'lsat'
                ? 'NON-DISCLOSURE AGREEMENT AND GENERAL TERMS OF USE FOR PATH BUDDY LSAT PRACTICE'
                : 'NON-DISCLOSURE AGREEMENT AND GENERAL TERMS OF USE FOR PATH BUDDY LNAT MOCK EXAMS'}
            </p>
            <p style={{ margin: '0 0 12px', color: 'var(--iv-body)' }}>
              {lsatPractice || ndaVariant === 'lsat'
                ? 'This LSAT practice is confidential. It is available to you as a candidate solely for your own preparation and assessment in this sitting. You are prohibited from disclosing, publishing, reproducing, or transmitting this exam, in whole or in part, by any means, without Path Buddy’s prior permission.'
                : 'This mock exam is confidential. It is available to you as a candidate solely for your own preparation and assessment in this sitting. You are prohibited from disclosing, publishing, reproducing, or transmitting this exam, in whole or in part, by any means, without Path Buddy’s prior permission.'}
            </p>
            <p style={{ margin: '0 0 18px', color: 'var(--iv-body)' }}>
              Personal data and test data from this sitting are stored in Path Buddy systems.
            </p>
            <p style={{ margin: '0 0 12px', textAlign: 'center', fontWeight: 700, color: 'var(--iv-body)' }}>
              Acknowledgements
            </p>
            <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
              Passages are reproduced by kind permission of the copyright holders where applicable.
            </p>
            <p style={{ margin: 0, color: 'var(--iv-body)' }}>
              Every effort has been made to contact copyright holders. We will be happy to rectify any omission of acknowledgement.
            </p>
          </div>
        ) : (
        <div style={{ width: '100%', textAlign: 'left' }}>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            Welcome to the <strong>{examHeading || getPearsonPlayerExamTitle(examName, undefined, { tsaSitting, heritageExam })}</strong>.
          </p>
          <p style={{ margin: '0 0 10px', color: '#000' }}>
            This sitting is for your own preparation. Do not share, publish, or redistribute these items, in whole or in part, by any means.
          </p>
          <p style={{ margin: '0 0 10px', color: 'var(--iv-body)' }}>
            {disclaimerCopy
              || (ndaVariant === 'bmat'
                ? 'We are not Cambridge Assessment or Pearson, and this sitting is not an official test.'
                : isLnat
                  ? 'We are not Pearson VUE or the LNAT Consortium, and this sitting is not an official test.'
                  : 'We are not University Admissions Tests UK (UAT-UK) or Pearson VUE, and this sitting is not an official test.')}
          </p>
          <p style={{ margin: 0, color: '#000' }}>
            Click the <strong>Next (N)</strong> button when you are ready to begin the test.
          </p>
        </div>
        )}
      </div>
      {readyToBeginOpen ? (
        <TmuaPearsonDialog
          title="Ready to Begin"
          info
          onYes={onReadyYes}
          onNo={onReadyNo}
        >
          Are you ready to begin this exam?
        </TmuaPearsonDialog>
      ) : null}
      {showEndExam ? (
        <TmuaPearsonDialog
          title="End Exam"
          info
          onYes={onEndExam}
          onNo={() => setShowEndExam(false)}
        >
          You have chosen to end this exam. Are you sure you want to end this exam?
        </TmuaPearsonDialog>
      ) : null}
    </TmuaPearsonShell>
  );
};

export default TmuaWelcomeScreen;
